import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useAppData } from '../../context/AppContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { usePermissions } from '../../hooks/usePermissions'
import { useTour } from '../../context/TourContext'
// import {
//   Home, Users, FolderOpen, UserCheck, Calendar, Clock,
//   BarChart3, DollarSign, Building, Settings, LogOut, UserCircle, Shield, AlertTriangle, Database, CheckSquare, Umbrella, HistoryIcon
// } from '../icons/Icons'
import {
  Home, Users, FolderOpen, UserCheck, Calendar, Clock,
  BarChart3, DollarSign, Building, Settings, LogOut, UserCircle, Shield, AlertTriangle, Database, CheckSquare, Umbrella, HistoryIcon, Menu, ClipboardList
} from '../icons/Icons'
import { SubscriptionLimits } from '../../services/subscriptionService'
import { TourOverlay } from '../ui/TourOverlay'
import { ReactivationFlow } from '../features/ReactivationFlow'
import TermsUpdateModal from '../features/TermsUpdateModal'
import { TrialExpirationModal } from '../features/TrialExpirationModal'
import Dashboard from '../features/Dashboard'
import Customers from '../features/Customers'
import Partners from '../features/Partners'
import PartnerRateSheets from '../features/PartnerRateSheets'
import Projects from '../features/Projects'
import Resources from '../features/Resources'
import ResourceRoles from '../features/ResourceRoles'
import Allocations from '../features/Allocations'
import Reports from '../features/Reports'
import CurrencySettings from '../features/CurrencySettings'
import Holidays from '../features/Holidays'
import TimeOff from '../features/TimeOff'
import DataGovernanceDashboard from '../features/DataGovernanceDashboard'
import DepartmentManagement from '../features/DepartmentManagement'
import TaskManagement from '../features/TaskManagement'
import MyTasks from '../features/MyTasks'
import WeekTimesheet from '../features/WeekTimesheet'
import TimesheetApprovals from '../features/TimesheetApprovals'
import TimesheetHistory from '../features/TimesheetHistory'
import Timesheets from '../features/Timesheets'
import styles from './AppShell.module.css'

import { UserType } from '../../types'

interface TabDef {
  id: string
  label: string
  icon: React.FC<any>
  requires?: keyof SubscriptionLimits
  visibleTo: UserType[]
}

const ALL_USERS: UserType[] = ['owner', 'admin', 'manager', 'contributor', 'viewer', 'resource']
const STAFF: UserType[] = ['owner', 'admin', 'manager', 'contributor', 'viewer']
const MANAGERS_UP: UserType[] = ['owner', 'admin', 'manager']
const ADMINS_UP: UserType[] = ['owner', 'admin']

const tabs: TabDef[] = [
  { id: 'dashboard',     label: 'Dashboard',           icon: Home,        visibleTo: STAFF },
  { id: 'customers',     label: 'Customers',            icon: Users,       visibleTo: STAFF },
  { id: 'partners',      label: 'Partners',             icon: Building,    visibleTo: STAFF },
  { id: 'partnerRates',  label: 'Partner Rates',        icon: DollarSign,  visibleTo: STAFF, requires: 'has_partner_rates' },
  { id: 'projects',      label: 'Projects',             icon: FolderOpen,  visibleTo: STAFF },
  { id: 'tasks',         label: 'Tasks',                icon: CheckSquare, visibleTo: STAFF },
  { id: 'mytasks',       label: 'My Tasks',             icon: ClipboardList, visibleTo: ALL_USERS },
  { id: 'resources',     label: 'Resources',            icon: UserCheck,   visibleTo: STAFF },
  { id: 'roleTypes',     label: 'Role Types',           icon: Settings,    visibleTo: MANAGERS_UP },
  { id: 'allocations',   label: 'Allocations',          icon: Calendar,    visibleTo: STAFF },
  { id: 'weektimesheet', label: 'Weekly Timesheet',     icon: Clock,       visibleTo: ALL_USERS },
  { id: 'tsApprovals',   label: 'Timesheet Approvals',  icon: CheckSquare, visibleTo: MANAGERS_UP },
  { id: 'timesheets',     label: 'Resource Timesheets', icon: Clock,       visibleTo: MANAGERS_UP },
  { id: 'tsHistory',      label: 'Timesheet History',   icon: HistoryIcon, visibleTo: ALL_USERS },
  { id: 'reports',       label: 'Reports',              icon: BarChart3,   visibleTo: STAFF },
  { id: 'departments',   label: 'Departments',          icon: Building,    visibleTo: ADMINS_UP },
  { id: 'currency',      label: 'Currency',             icon: DollarSign,  visibleTo: ADMINS_UP },
  { id: 'holidays',      label: 'Holidays',             icon: Calendar,    visibleTo: ADMINS_UP },
  { id: 'timeoff',       label: 'Time Off',             icon: Umbrella,    visibleTo: ALL_USERS },
  { id: 'governance',    label: 'Data Governance',      icon: Database,    visibleTo: ADMINS_UP },
]

export default function AppShell() {
  const { organization, orgMember, user, signOut } = useAuth()
  const { loading, refreshData, pendingApprovalsForMe } = useAppData()
  const { subscription, daysLeft, canAccess, lifecycleState, gracePeriodDaysLeft, isReadOnly, canReactivate } = useSubscription()
  const { isAdmin, userType } = usePermissions()
  const { isTourActive, startTour, registerSidebarTabSetter, setSidebarTab } = useTour()
  const visibleTabs = tabs.filter(t => t.visibleTo.includes(userType))
  const defaultTab = visibleTabs[0]?.id ?? 'weektimesheet'
  const [activeTab, setActiveTab] = useState(defaultTab)

  useEffect(() => {
    registerSidebarTabSetter((tabId: string) => {
      setActiveTab(tabId)
      setSidebarCollapsed(false)
    })
  }, [registerSidebarTabSetter])
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showReactivation, setShowReactivation] = useState(false)
  const [showTrialExpiration, setShowTrialExpiration] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('allocatiq_sidebar_collapsed') === 'true'
    } catch {
      return false
    }
  })

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev
      try {
        localStorage.setItem('allocatiq_sidebar_collapsed', String(next))
      } catch {
        /* ignore storage errors */
      }
      return next
    })
  }


  useEffect(() => {
    if (lifecycleState === 'trial_expired') {
      setShowTrialExpiration(true)
    }
  }, [lifecycleState])

  useEffect(() => {
    if (organization?.id) {
      console.log('[AppShell] Loading data for organization:', organization.name)
      refreshData()
    }
  }, [organization?.id, refreshData])

  useEffect(() => {
    try {
      localStorage.setItem('allocatiq_user_type', userType)
    } catch { /* ignore */ }
  }, [userType])

  useEffect(() => {
    const onboardingCompleted =
      (user as any)?.onboarding_completed === true ||
      localStorage.getItem('allocatiq_onboarding_completed') === 'true'
    if (!onboardingCompleted && !isTourActive) {
      const timer = setTimeout(() => {
        startTour()
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [user, isTourActive, startTour])

  const renderLockedPage = (feature: string) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 32px', textAlign: 'center', color: '#605e5c' }}>
      <Shield size={40} style={{ color: '#c8c6c4', marginBottom: 16 }} />
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#323130', margin: '0 0 8px' }}>Feature not available</h2>
      <p style={{ fontSize: 14, maxWidth: 380, lineHeight: 1.6, marginBottom: 24 }}>
        <strong>{feature}</strong> is not included in your current plan. Upgrade to access this feature.
      </p>
      {isAdmin
        ? <Link to="/admin" style={{ display: 'inline-block', padding: '8px 20px', background: '#0078d4', color: '#fff', borderRadius: 3, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>View upgrade options →</Link>
        : <p style={{ fontSize: 13, color: '#a19f9d' }}>Contact your administrator to upgrade the plan.</p>
      }
    </div>
  )

  const renderContent = () => {
    const tab = visibleTabs.find(t => t.id === activeTab)
    if (!tab) return null
    if (tab.requires && !canAccess(tab.requires)) {
      return renderLockedPage(tab.label)
    }
    switch (activeTab) {
      case 'dashboard': return <Dashboard />
      case 'customers': return <Customers />
      case 'partners': return <Partners />
      case 'partnerRates': return <PartnerRateSheets />
      case 'projects': return <Projects />
      case 'tasks': return <TaskManagement />
      case 'mytasks': return <MyTasks />
      case 'resources': return <Resources />
      case 'roleTypes': return <ResourceRoles />
      case 'allocations': return <Allocations />
      case 'weektimesheet': return <WeekTimesheet />
      case 'tsApprovals': return <TimesheetApprovals />
      case 'timesheets': return <Timesheets />
      case 'tsHistory': return <TimesheetHistory />
      case 'reports': return <Reports />
      case 'departments': return <DepartmentManagement />
      case 'currency': return <CurrencySettings />
      case 'holidays': return <Holidays />
      case 'timeoff': return <TimeOff />
      case 'governance': return <DataGovernanceDashboard />
      default: return <Dashboard />
    }
  }

  if (loading) {
    return (
      <div className={styles.loadingScreen}>
        <div className={styles.spinner} />
        <p>Loading {organization?.name || 'workspace'}...</p>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <TermsUpdateModal />
      <TourOverlay />
      {showTrialExpiration && (
        <TrialExpirationModal
          onConverted={() => {
            setShowTrialExpiration(false)
          }}
        />
      )}
      {lifecycleState === 'grace_period' && gracePeriodDaysLeft !== null && (
        <div style={{
          background: gracePeriodDaysLeft <= 5 ? '#fde7e9' : '#fff4ce',
          borderBottom: `1px solid ${gracePeriodDaysLeft <= 5 ? '#f3b8a8' : '#ffd97d'}`,
          padding: '8px 20px',
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
        }}>
          <AlertTriangle size={16} style={{ color: gracePeriodDaysLeft <= 5 ? '#d13438' : '#f7630c', flexShrink: 0 }} />
          <span style={{ color: '#323130', fontWeight: 500 }}>
            <strong>Account in Grace Period</strong> — {gracePeriodDaysLeft} day{gracePeriodDaysLeft === 1 ? '' : 's'} remaining until suspension.
            <span style={{ fontWeight: 400 }}> Your account is now read-only.</span>
          </span>
          <button
            onClick={() => setShowReactivation(true)}
            style={{
              padding: '6px 16px',
              background: '#0078d4',
              color: 'white',
              border: 'none',
              borderRadius: 3,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            Reactivate Now
          </button>
        </div>
      )}

      {lifecycleState === 'suspended' && (
        <div style={{
          background: '#fde7e9',
          borderBottom: '1px solid #d13438',
          padding: '8px 20px',
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
        }}>
          <AlertTriangle size={16} style={{ color: '#d13438', flexShrink: 0 }} />
          <span style={{ color: '#323130', fontWeight: 500 }}>
            <strong>Account Suspended</strong> — Your account is in read-only mode. All data is preserved.
          </span>
          {canReactivate && (
            <button
              onClick={() => setShowReactivation(true)}
              style={{
                padding: '6px 16px',
                background: '#d13438',
                color: 'white',
                border: 'none',
                borderRadius: 3,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              Reactivate Account
            </button>
          )}
        </div>
      )}

      {subscription?.status === 'trialing' && daysLeft !== null && lifecycleState === 'trialing' && (
        <div style={{
          background: daysLeft <= 3 ? '#fde7e9' : daysLeft <= 7 ? '#fff4ce' : '#deecf9',
          borderBottom: `1px solid ${daysLeft <= 3 ? '#f3b8a8' : daysLeft <= 7 ? '#ffd97d' : '#b3d4f0'}`,
          padding: '6px 20px',
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
        }}>
          <span style={{ color: daysLeft <= 3 ? '#d13438' : '#323130' }}>
            {daysLeft === 0
              ? <strong>Your free trial has expired.</strong>
              : <>Free trial — <strong>{daysLeft} day{daysLeft === 1 ? '' : 's'} remaining</strong></>
            }
          </span>
          {isAdmin && (
            <button
              onClick={() => setShowTrialExpiration(true)}
              style={{
                padding: '4px 12px',
                background: daysLeft <= 3 ? '#d13438' : '#0078d4',
                color: '#fff',
                border: 'none',
                borderRadius: 3,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Choose a plan
            </button>
          )}
        </div>
      )}

      <ReactivationFlow isOpen={showReactivation} onClose={() => setShowReactivation(false)} />

      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.brand}>
            <svg className={styles.brandIcon} viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M10 26L18 10L26 26" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 21H23" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            <div>
              <span className={styles.brandName}>AllocatIQ</span>
              {organization && <span className={styles.orgName}>{organization.name}</span>}
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.userMenu}>
            <button
              className={styles.userBtn}
              onClick={() => setShowUserMenu(v => !v)}
            >
              <UserCircle size={20} />
              <span>{orgMember?.display_name || user?.email}</span>
              {orgMember && <span className={styles.rolePill}>{orgMember.role}</span>}
            </button>
            {showUserMenu && (
              <div className={styles.dropdown} onClick={() => setShowUserMenu(false)}>
                <div className={styles.dropdownEmail}>{user?.email}</div>
                {isAdmin && (
                  <Link to="/admin" className={styles.dropdownItem} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Settings size={14} />
                    Admin Panel
                  </Link>
                )}
                <button className={styles.dropdownItem} onClick={() => { setShowUserMenu(false); startTour(); }}>
                  <Settings size={14} />
                  Take the product tour
                </button>
                <button className={styles.dropdownItem} onClick={signOut}>
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className={styles.body}>
        {/* <aside className={styles.sidebar}>
          <nav className={styles.nav}>
            {visibleTabs.map(tab => {
              const Icon = tab.icon
              const locked = tab.requires ? !canAccess(tab.requires) : false
              return (
                <button
                  key={tab.id}
                  className={`${styles.navItem} ${activeTab === tab.id ? styles.active : ''} ${locked ? styles.navLocked : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  title={locked ? 'Upgrade your plan to access this feature' : undefined}
                >
                  <Icon size={15} />
                  <span>{tab.label}</span>
                  {locked && <Shield size={12} style={{ marginLeft: 'auto', opacity: 0.5 }} />}
                  {!locked && tab.id === 'timeoff' && pendingApprovalsForMe.length > 0 && (
                    <span className={styles.navBadge}>{pendingApprovalsForMe.length}</span>
                  )}
                </button>
              )
            })}
          </nav>
        </aside> */}
                <aside className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}>
          <button
            className={styles.collapseToggle}
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Menu size={20} />
          </button>
          <nav className={styles.nav}>
            {visibleTabs.map(tab => {
              const Icon = tab.icon
              const locked = tab.requires ? !canAccess(tab.requires) : false
              return (
                <button
                  key={tab.id}
                  className={`${styles.navItem} ${activeTab === tab.id ? styles.active : ''} ${locked ? styles.navLocked : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  title={locked ? 'Upgrade your plan to access this feature' : (sidebarCollapsed ? tab.label : undefined)}
                  data-tour-tab={tab.id}
                >
                  <Icon size={18} />
                  {!sidebarCollapsed && <span>{tab.label}</span>}
                  {locked && !sidebarCollapsed && <Shield size={12} style={{ marginLeft: 'auto', opacity: 0.5 }} />}
                  {!locked && tab.id === 'timeoff' && pendingApprovalsForMe.length > 0 && !sidebarCollapsed && (
                    <span className={styles.navBadge}>{pendingApprovalsForMe.length}</span>
                  )}
                </button>
              )
            })}
          </nav>
        </aside>


        <main className={styles.main}>
          <div className={styles.content}>
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  )
}
